import { Pressable, View } from 'react-native';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';

export type ToastVariant = 'default' | 'success' | 'destructive';

const variantIcon: Record<ToastVariant, LucideIcon> = {
  default: Info,
  success: CircleCheck,
  destructive: CircleAlert,
};

const variantIconClassName: Record<ToastVariant, string> = {
  default: 'text-text-secondary',
  success: 'text-positive',
  destructive: 'text-destructive',
};

export interface ToastProps {
  variant?: ToastVariant;
  title: string;
  description?: string;
  /** Accessibility label for the dismiss button. */
  dismissLabel: string;
  onDismiss: () => void;
}

/**
 * A single toast card. Presentational only — the {@link ToastProvider} owns the
 * stack, timing and animation.
 */
export const Toast = ({
  variant = 'default',
  title,
  description,
  dismissLabel,
  onDismiss,
}: ToastProps) => (
  <View className="w-full max-w-md flex-row items-center gap-3 rounded-xl border border-border bg-surface-elevated px-4 py-3 shadow-lg">
    <Icon
      as={variantIcon[variant]}
      size={20}
      className={variantIconClassName[variant]}
    />
    <View className="flex-1 gap-0.5">
      <Text className="font-inter-medium text-sm text-text-primary">
        {title}
      </Text>
      {description ? (
        <Text className="font-inter-regular text-xs text-text-secondary">
          {description}
        </Text>
      ) : null}
    </View>
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={dismissLabel}
      onPress={onDismiss}
      hitSlop={8}
      className="p-1 active:opacity-70"
    >
      <Icon as={X} size={16} className="text-text-muted" />
    </Pressable>
  </View>
);
